export type StateFilter =
  | "total"
  | "vigente"
  | "vencido"
  | "moroso"
  | "inactivo"
  | "suspendido";

export type NewClientsRange = "lastMonth" | "lastWeek" | "yesterday" | "today";

export type ClientDebtState = "vigente" | "vencido" | "moroso" | "inactivo";

export type ResidenceStateOption = {
  value: string;
  label: string;
};

export type RegistrationListAttribution = {
  key: string;
  label: string;
};

export type RegistrationListOption = {
  value: string;
  label: string;
  totalUsers?: number;
};

export type IndicatorKey = "total" | "vigentes" | "inactivos" | "vencidos" | "morosos";

export type IndicatorBreakdown = Record<IndicatorKey, number>;

export type NewClientsBreakdown = Record<NewClientsRange, number>;

export type ClientLevelBreakdown = Record<
  "total" | "level1" | "level2" | "level3" | "level4" | "level5",
  number
>;

export type CreditAmountLabels = {
  limit: string;
  available: string;
  used: string;
};

export type AmountDetail = {
  count?: number;
  amountUsd: number;
  percentage: number;
};

export interface ClientRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  document: string;
  identificationType?: string;
  documentNumber?: string;
  status: string;
  statusName: string;
  debtState: ClientDebtState;
  debtStateName: string;
  image: string | null;
  registrationLists: RegistrationListAttribution[];
  missingProfilePicture: boolean;
  missingDocumentPicture: boolean;
  maxAmount: number | null;
  creditoDisponible: number | null;
  creditoUsado: number | null;
  cuotasVencidas?: number;
  montoVencidoUsd?: number;
  lastPaymentAt?: string | null;
  daysInArrears?: number;
  createdAt?: string | null;
}

export interface ClientStatistics {
  total: number;
  vigentes: number;
  inactivos: number;
  vencidos: number;
  morosos: number;
  morososSinGrupo: number;
  // Clients whose account is suspended (banned), and their share of the total.
  suspendidos: number;
  suspendidosPercentage: number;
  newClients: NewClientsBreakdown;
  levels: ClientLevelBreakdown;
  amounts: IndicatorBreakdown;
  portfolioDistributionAmounts: IndicatorBreakdown;
  amountPercentages: IndicatorBreakdown;
  amountDetails: {
    morosos: {
      cuotasMorosas: AmountDetail;
      cuotasVigentes: AmountDetail;
      cuotasVencidas: AmountDetail;
    };
  };
  percentages: IndicatorBreakdown;
}

export interface ClientListUserResponse {
  _id: string;
  name?: string;
  lastname?: string;
  email?: string;
  phone?: unknown;
  picture?: string;
  document?: string | number;
  status?: string;
  statusName?: string;
  debtState?: ClientDebtState;
  debtStateName?: string;
  identificationType?: string;
  image?: string | null;
  registrationLists?: RegistrationListAttribution[];
  missingProfilePicture?: boolean;
  missingDocumentPicture?: boolean;
  maxAmount?: number | null;
  creditoDisponible?: number | null;
  creditoUsado?: number | null;
  cuotasVencidas?: number;
  montoVencidoUsd?: number;
  lastPaymentAt?: string | null;
  daysInArrears?: number;
  createdAt?: string | null;
}

export const validStateFilters: StateFilter[] = [
  "total",
  "vigente",
  "vencido",
  "moroso",
  "inactivo",
  "suspendido",
];

export const validNewClientsRanges: NewClientsRange[] = [
  "lastMonth",
  "lastWeek",
  "yesterday",
  "today",
];

export const emptyIndicatorBreakdown: IndicatorBreakdown = {
  total: 0,
  vigentes: 0,
  inactivos: 0,
  vencidos: 0,
  morosos: 0,
};

export const emptyNewClientsBreakdown: NewClientsBreakdown = {
  lastMonth: 0,
  lastWeek: 0,
  yesterday: 0,
  today: 0,
};

export const emptyClientLevelBreakdown: ClientLevelBreakdown = {
  total: 0,
  level1: 0,
  level2: 0,
  level3: 0,
  level4: 0,
  level5: 0,
};

export const getEmptyClientStatistics = (): ClientStatistics => ({
  total: 0,
  vigentes: 0,
  inactivos: 0,
  vencidos: 0,
  morosos: 0,
  morososSinGrupo: 0,
  suspendidos: 0,
  suspendidosPercentage: 0,
  newClients: { ...emptyNewClientsBreakdown },
  levels: { ...emptyClientLevelBreakdown },
  amounts: { ...emptyIndicatorBreakdown },
  portfolioDistributionAmounts: { ...emptyIndicatorBreakdown },
  amountPercentages: { ...emptyIndicatorBreakdown },
  amountDetails: {
    morosos: {
      cuotasMorosas: {
        amountUsd: 0,
        percentage: 0,
      },
      cuotasVigentes: {
        count: 0,
        amountUsd: 0,
        percentage: 0,
      },
      cuotasVencidas: {
        count: 0,
        amountUsd: 0,
        percentage: 0,
      },
    },
  },
  percentages: { ...emptyIndicatorBreakdown },
});
